import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, RefreshCw, ClipboardList, FlaskConical } from "lucide-react";
import { statusBadgeLabel } from "./productionMilestones";
import { adminFetch } from "@/lib/adminApi";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 15000;

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
}

function badgeColorFor(label) {
  // Loose palette-mapping to help admins scan the queue.
  if (label === "Delivered")               return { bg: "#DCFCE7", fg: "#166534" };
  if (label === "Report Complete")         return { bg: "#DBEAFE", fg: "#1E40AF" };
  if (label === "Approved for Assembly")   return { bg: "#EDE9FE", fg: "#5B21B6" };
  if (label === "Ready for Human Review")  return { bg: "#FEF3C7", fg: "#92400E" };
  if (label === "Photos Received")         return { bg: "#E0F2FE", fg: "#075985" };
  if (label === "Waiting for Photos")      return { bg: "#FEE2E2", fg: "#991B1B" };
  if (label === "Drafting Creative")       return { bg: "#F3F4F6", fg: "#374151" };
  return { bg: "#F3F0FB", fg: "#6A4FC0" };
}

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [showTest, setShowTest] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const load = async (q, includeTest) => {
    try {
      const url = new URL(`${API}/api/orders`);
      if (q && q.trim()) url.searchParams.set("search", q.trim());
      if (!includeTest) url.searchParams.set("include_test", "false");
      const res = await adminFetch(url.toString());
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setOrders(Array.isArray(data) ? data : []);
      setError("");
    } catch (e) {
      setError(e.message || "Failed to load orders");
    } finally {
      setLoading(false);
    }
  };

  // Debounced search + auto-poll every 15s
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(() => { if (!cancelled) load(search, showTest); }, 250);
    const poll = setInterval(() => { if (!cancelled) load(search, showTest); }, POLL_MS);
    return () => { cancelled = true; clearTimeout(t); clearInterval(poll); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, showTest]);

  return (
    <div style={{ padding: 32 }} data-testid="admin-orders-page">
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <ClipboardList size={22} color="#6A4FC0" />
            <h1 style={{ fontSize: 24, fontWeight: 700, color: "#2A2540", margin: 0 }}>
              Orders
            </h1>
          </div>
          <div style={{ fontSize: 13, color: "#7A6E93", marginTop: 4 }}>
            Live production queue — auto-refreshes every 15 seconds.
          </div>
        </div>
        <button
          type="button"
          data-testid="orders-refresh-btn"
          onClick={() => load(search, showTest)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "8px 14px", border: "1px solid #E1DDF0",
            background: "#fff", borderRadius: 10, color: "#6A4FC0",
            cursor: "pointer", fontWeight: 600, fontSize: 13,
          }}
        >
          <RefreshCw size={15} /> Refresh
        </button>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20, flexWrap: "wrap" }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 8, background: "#fff",
          border: "1px solid #ECEAF3", borderRadius: 12, padding: "10px 14px",
          flex: "1 1 280px", maxWidth: 480,
        }}>
          <Search size={16} color="#9A93B0" />
          <input
            data-testid="orders-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search order #, customer, email, or pet name"
            style={{ border: 0, outline: "none", flex: 1, fontSize: 14, background: "transparent", color: "#2A2540" }}
          />
        </div>
        <label
          data-testid="orders-show-test-toggle"
          style={{
            display: "inline-flex", alignItems: "center", gap: 8,
            fontSize: 13, color: "#6E6880", fontWeight: 600, cursor: "pointer",
            userSelect: "none",
          }}
        >
          <input
            type="checkbox"
            data-testid="orders-show-test-checkbox"
            checked={showTest}
            onChange={(e) => setShowTest(e.target.checked)}
            style={{ width: 16, height: 16, accentColor: "#B45309" }}
          />
          Show test orders
        </label>
      </div>

      {error && (
        <div data-testid="orders-error" style={{ padding: 14, background: "#FEE2E2", color: "#991B1B", borderRadius: 10, marginBottom: 20 }}>
          {error}
        </div>
      )}

      <div style={{ background: "#fff", border: "1px solid #ECEAF3", borderRadius: 14, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead style={{ background: "#F7F6FB", color: "#6E6880" }}>
            <tr>
              <Th>Order #</Th>
              <Th>Customer</Th>
              <Th>Pet(s)</Th>
              <Th>Order Date</Th>
              <Th>Stage</Th>
            </tr>
          </thead>
          <tbody data-testid="orders-table-body">
            {loading && orders.length === 0 && (
              <tr><td colSpan={5} style={{ padding: 24, textAlign: "center", color: "#9A93B0" }}>Loading…</td></tr>
            )}
            {!loading && orders.length === 0 && (
              <tr><td colSpan={5} data-testid="orders-empty" style={{ padding: 24, textAlign: "center", color: "#9A93B0" }}>
                No orders match your search yet.
              </td></tr>
            )}
            {orders.map((o) => {
              const label = statusBadgeLabel(o);
              const c = badgeColorFor(label);
              return (
                <tr
                  key={o.order_number}
                  data-testid={`orders-row-${o.order_number}`}
                  onClick={() => navigate(`/admin/orders/${o.order_number}`)}
                  style={{ cursor: "pointer", borderTop: "1px solid #F3F0FB" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "#FAFAFF")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <Td>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                      <Link
                        to={`/admin/orders/${o.order_number}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: "#6A4FC0", fontWeight: 700, textDecoration: "none" }}
                        data-testid={`orders-link-${o.order_number}`}
                      >
                        {o.order_number}
                      </Link>
                      {o.is_test ? (
                        <span
                          data-testid={`orders-test-badge-${o.order_number}`}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 3,
                            padding: "2px 7px", borderRadius: 999,
                            background: "#FEF3C7", color: "#78350F",
                            border: "1px solid #FCD34D",
                            fontSize: 10, fontWeight: 800, letterSpacing: 0.5,
                          }}
                        >
                          <FlaskConical size={10} /> TEST
                        </span>
                      ) : null}
                    </div>
                  </Td>
                  <Td>
                    <div style={{ fontWeight: 600 }}>{o.customer_name}</div>
                    <div style={{ fontSize: 12, color: "#9A93B0" }}>{o.customer_email}</div>
                  </Td>
                  <Td>{o.pet_names || "—"}</Td>
                  <Td>{fmtDate(o.created_at)}</Td>
                  <Td>
                    <span
                      data-testid={`orders-stage-${o.order_number}`}
                      style={{
                        display: "inline-block", padding: "4px 10px", borderRadius: 999,
                        background: c.bg, color: c.fg, fontWeight: 700, fontSize: 12,
                      }}
                    >
                      {label}
                    </span>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const Th = ({ children }) => (
  <th style={{ textAlign: "left", padding: "12px 16px", fontWeight: 700, fontSize: 12, letterSpacing: 1, textTransform: "uppercase" }}>
    {children}
  </th>
);

const Td = ({ children }) => (
  <td style={{ padding: "14px 16px", verticalAlign: "top", color: "#2A2540" }}>{children}</td>
);
